import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SPAButton from '../shared/SPAButton';
import SPADialog from '../shared/SPADialog';
import RoutedSPADialog from '../shared/RoutedSPADialog';
import SPAPill from '../shared/SPAPill';
import SPARowList from '../shared/SPARowList';
import type { SPAPillTone } from '../shared/SPAPill';
import { useSPAUser } from '../shared/SPAUser';
import useAssignmentSyncState from './useAssignmentSyncState';
import useStudentHistory from './useStudentHistory';
import type { HistoryEvent, HistoryOutcome } from './useStudentHistory';
import { REASON_LABELS, STATE_LABELS, STATE_TONES } from './syncStates';

const TONES: Record<HistoryOutcome, SPAPillTone> = {
  accepted: 'success',
  link_failed: 'error',
  link_only: 'neutral',
  lookup: 'neutral',
  not_sent: 'warning',
  opened: 'info',
  refused: 'error',
};

const LABELS: Record<HistoryOutcome, string> = {
  accepted: 'Accepted',
  link_failed: "Couldn't link",
  link_only: 'Work link',
  lookup: 'Lookup',
  not_sent: 'Not sent',
  opened: 'Opened',
  refused: 'Refused',
};

const scoreOf = (event: HistoryEvent) => {
  if (event.score_given == null) return null;

  return event.score_maximum == null ? `${event.score_given}` : `${event.score_given}/${event.score_maximum}`;
};

const courseName = (event: HistoryEvent) => event.course_title ?? `course ${event.course}`;

const sentence = (event: HistoryEvent) => {
  const score = scoreOf(event);
  const where = event.course == null ? '' : ` to ${courseName(event)}`;
  const why = event.reason == null ? '' : REASON_LABELS[event.reason] ?? event.reason;

  switch (event.outcome) {
    case 'accepted': return `Sent ${score}${where} — the LMS took it`;
    // A scoreless Canvas post: the SpeedGrader link to the student's work (Submission#should_send_speedgrader_url?).
    case 'link_only': return `Gave ${event.course == null ? 'the LMS' : courseName(event)} a link to the `
      + "student's TCI work for SpeedGrader — no score yet";
    case 'refused': return `${score == null ? 'Sent' : `Sent ${score}`}${where} — refused: ${why}`;
    case 'not_sent': return `Not sent: ${why}`;
    case 'link_failed': return 'Opened it from the LMS, but it could not be linked';
    case 'opened': return `Opened it from ${event.course_title ?? `LMS course ${event.course}`}`;
    default: return `Looked up the LMS (${event.method ?? 'GET'} ${event.status ?? ''})`.trim();
  }
};

const EventLine = ({ event }: { event: HistoryEvent }) => (
  <div className="tw-flex tw-gap-3 tw-text-sm">
    <span className="tw-w-44 tw-shrink-0 tw-tabular-nums tw-text-spa-secondary">
      {new Date(event.at).toLocaleString()}
    </span>
    <span className="tw-w-20 tw-shrink-0"><SPAPill tone={TONES[event.outcome]}>{LABELS[event.outcome]}</SPAPill></span>
    <span className="tw-min-w-0">
      <span className="tw-text-spa-primary">{sentence(event)}</span>
      {event.detail != null && (
        <span className="tw-block tw-break-words tw-text-sm tw-text-spa-secondary">
          {event.outcome === 'link_failed' ? event.detail : `LMS said ${event.detail}`}
        </span>
      )}
    </span>
  </div>
);

// AIDEV-NOTE: The raw log answers "was it sent, did the LMS take it, why not" only to someone who reads
// LTI JSON. This answers the same three per event, from LmsGradeSyncStudentHistoryService, with the
// student's opens of the assignment interleaved, and keeps the raw log one click away for Sysadmins.
// Lookups (GETs) are context, so they start hidden.
const StudentHistory = () => {
  const navigate = useNavigate();
  const { canViewSyncLog } = useSPAUser();
  const { id: assignmentId = '', studentId = '' } = useParams();
  const { data, error, isFetching } = useStudentHistory(assignmentId, studentId);
  const { data: assignment } = useAssignmentSyncState(assignmentId);
  const [showLookups, setShowLookups] = useState(false);

  const row = assignment?.students.find(s => String(s.student_id) === studentId);
  const events = data?.events ?? [];
  const lookups = events.filter(event => event.outcome === 'lookup').length;
  const shown = showLookups ? events : events.filter(event => event.outcome !== 'lookup');

  return (
    <RoutedSPADialog returnPath="..">
      <SPADialog.Content size="lg">
        <SPADialog.Header>
          <SPADialog.Title>{data?.student.name ?? row?.student_name ?? `Student ${studentId}`}</SPADialog.Title>
          <SPADialog.Description>
            {row == null ? 'Grade sync history on this assignment, newest first.' : (
              <>
                <SPAPill tone={STATE_TONES[row.state]}>{STATE_LABELS[row.state]}</SPAPill>
                {` Score in TCI ${row.current_score ?? '—'} · last delivered ${row.synced_score ?? '—'}`}
                {row.synced_at != null && ` on ${new Date(row.synced_at).toLocaleString()}`}
              </>
            )}
          </SPADialog.Description>
        </SPADialog.Header>

        <SPADialog.Body>
          {error != null && <p className="tw-m-0 tw-text-sm tw-text-spa-error">Couldn&apos;t load the history.</p>}
          {isFetching && events.length === 0 && <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">Loading…</p>}
          {!isFetching && error == null && events.length === 0 && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
              Nothing has ever been sent for this student on this assignment.
            </p>
          )}
          {shown.length > 0 && (
            <SPARowList footer={data?.truncated === true && 'Showing the latest 100 events.'}>
              {shown.map(event => <EventLine key={event.id} event={event} />)}
            </SPARowList>
          )}
        </SPADialog.Body>

        <SPADialog.Footer>
          {lookups > 0 && (
            <SPAButton size="sm" type="button" variant="secondary" onClick={() => setShowLookups(value => !value)}>
              {showLookups ? 'Hide lookups' : `Show ${lookups} lookups`}
            </SPAButton>
          )}
          {canViewSyncLog && (
            <SPAButton
              size="sm"
              type="button"
              variant="secondary"
              onClick={() => navigate(`../log?student_id=${studentId}`)}
            >
              Raw requests
            </SPAButton>
          )}
          <SPADialog.Close asChild>
            <SPAButton type="button" variant="secondary">Close</SPAButton>
          </SPADialog.Close>
        </SPADialog.Footer>
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default StudentHistory;
